<template>
  <Listbox
    id="user-and-contacts-autocomplete-component"
    v-model="selected_item"
    as="div"
    class="relative z-50"
    @focusin="focusInput"
    @focusout="unfocusInput">
    <ListboxLabel
      v-if="label"
      :class="[
        isBoxBackground
          ? 'bg-light-box-background dark:bg-dark-box-background'
          : 'bg-light-screen-background dark:bg-dark-screen-background',
        isInputFocused || search_term ? '-top-0 text-sm' : 'top-[20px] text-base',
        isInputFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
      ]"
      class="absolute z-30 px-1 transition-all ease-in-out transform -translate-y-1/2 rounded-full pointer-events-none select-none h-fit text-common-grey left-10 duration-side-menu-transition-duration">
      {{ label }}
    </ListboxLabel>
    <div class="z-10 flex mt-1 rounded-md shadow-sm">
      <div class="relative flex items-stretch flex-grow focus-within:z-10">
        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <Search class="w-5 h-5 text-gray-400" aria-hidden="true" />
        </div>
        <span v-if="loading" id="contacts-loading-component" class="absolute inset-y-0 flex items-center pr-2 pointer-events-none right-6">
          <RotateCw class="w-5 h-5 text-gray-500 animate-spin" aria-hidden="true" />
        </span>
        <input
          id="user-or-contact-input"
          ref="mainInput"
          v-model="search_term"
          :disabled="disabled"
          autocomplete="off"
          type="text"
          :autofocus="true"
          :class="[
            isBoxBackground
              ? 'bg-light-box-background dark:bg-dark-box-background'
              : 'bg-light-screen-background dark:bg-dark-screen-background',
            search_results.length > 0 && !disabled ? 'rounded-l-lg' : 'rounded-lg'
          ]"
          class="block w-full pl-10 text-sm transition ease-in-out border shadow-sm focus:shadow-outline disabled:bg-gray-200 border-light-divider-border dark:border-dark-divider-border placeholder-common-grey text-light-body-text dark:text-dark-body-text focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
          @click="
            () => {
              show_results = true
            }
          "
          @blur="
            () => {
              show_results = false
            }
          ">
      </div>
      <button
        v-show="!disabled"
        id="close-search-button"
        type="button"
        :class="
          isBoxBackground
            ? 'bg-light-box-background dark:bg-dark-box-background'
            : 'bg-light-screen-background dark:bg-dark-screen-background'
        "
        class="relative inline-flex items-center px-4 py-2 space-x-2 text-sm font-medium border border-light-divider-border dark:border-dark-divider-border rounded-r-md focus:outline-none"
        @click="reset">
        <X class="w-5 h-5 text-gray-400 hover:text-gray-700" aria-hidden="true" />
      </button>
    </div>
    <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
      <ListboxOptions
        id="user-panel"
        static
        :class="[
          isBoxBackground
            ? 'bg-light-box-background dark:bg-dark-box-background'
            : 'bg-light-screen-background dark:bg-dark-screen-background'
        ]"
        class="absolute z-50 w-full mt-1 overflow-auto text-base transition ease-in-out rounded-lg shadow-lg text-light-body-text dark:text-dark-body-text max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm duration-side-menu-transition-duration">
        <div v-if="search_results.length" class="flex justify-center mb-2 align-middle">
          <div v-if="inputLabels?.length" class="w-12 border-b" />
          <div v-if="inputLabels?.length" class="relative ml-3 mr-3 whitespace-nowrap top-2">
            {{ inputLabels[0].name }}
          </div>
          <div class="w-12 border-b" />
        </div>

        <div id="search-results-wrapper">
          <div v-for="item in search_results" :key="item.id">
            <ListboxOption
              v-if="
                !selectedContacts?.find((contact) => contact.id == item.id) &&
                  !excludedContacts?.find((contact) => (contact?.id ? contact.id : contact) == item.id)
              "
              as="template"
              :value="item">
              <li
                class="relative py-2 pl-8 pr-4 transition ease-in-out rounded-lg cursor-pointer text-light-body-text dark:text-light-divider hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue duration-side-menu-transition-duration">
                <div class="flex items-center">
                  <Avatar :user="Record.from(item)" />
                  <span :class="['font-normal ml-3 block truncate']">
                    {{ Record.from(item).name }}
                  </span>
                </div>
              </li>
            </ListboxOption>
          </div>
        </div>
        <div v-if="contacts.length" class="flex justify-center mb-2 align-middle">
          <div v-if="inputLabels?.length" class="w-16 border-b" />
          <div v-if="inputLabels?.length" class="relative ml-3 mr-3 whitespace-nowrap top-2">
            {{ inputLabels[1] ? inputLabels[1] : inputLabels[0] }}
          </div>
          <div class="w-16 border-b" />
        </div>
        <div id="contacts-results-wrapper">
          <div v-for="(item, i) in contacts" :key="item.id">
            <ListboxOption
              v-if="
                !selectedContacts?.find((contact) => contact.id == item.id) &&
                  !excludedContacts?.find((contact) => (contact?.id ? contact.id : contact) == item.id)
              "
              as="template"
              :value="item">
              <li
                class="relative py-2 pl-8 pr-4 transition ease-in-out rounded-lg cursor-pointer text-light-body-text dark:text-light-divider hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue duration-side-menu-transition-duration">
                <div :id="`contact-${i}`" class="flex items-center">
                  <Avatar :user="Record.from(item)" />
                  <span :class="['font-normal ml-3 block truncate']">
                    {{ Record.from(item).full_name }}
                  </span>
                </div>
              </li>
            </ListboxOption>
          </div>
        </div>
        <ListboxOption key="createNewContact" class="flex items-center justify-center">
          <div class="m-2 btn primary-btn" @click="handleAddNewContact">
            <div class="flex flex-nowrap">
              <Plus class="w-5 h-5 mr-2 text-white" />
              {{ $t("Add new contact") }}
            </div>
          </div>
        </ListboxOption>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script>
import { Record } from "@/models/record"
import { Listbox, ListboxOption, ListboxOptions, ListboxLabel } from "@headlessui/vue"
import { Search, X, Plus, RotateCw } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { VIEW_TYPES } from "@/store/constants/references"
import Avatar from "@/components/shared/Avatar.vue"

export default {
  components: {
    Avatar,
    Search,
    X,
    Plus,
    Listbox,
    ListboxOptions,
    ListboxOption,
    ListboxLabel,
    RotateCw
  },
  props: {
    excludedContacts: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    inputLabels: {
      type: Array,
      required: false
    },
    companies: {
      type: Array,
      required: false
    },

    // valueAttr: {
    //   type: String,
    //   required: false
    // },
    selectedContacts: {
      default: [],
      required: false
    },

    users: {
      default: [],
      required: true
    },

    contacts: {
      default: [],
      required: true
    },

    disabled: {
      type: Boolean,
      default: false
    },
    isBoxBackground: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      required: false
    }
  },
  emits: ["chosen", "reset", "changed"],

  data() {
    return {
      Record,
      show_results: false,
      search_term: null,
      search_results: [],
      selected_item: null,
      isInputFocused: false
    }
  },
  watch: {
    search_term() {
      this.$emit("changed", this.search_term)
      if (this.search_term != null) {
        this.search_results = this.users.filter((item) => {
          return Record.from(item).name.toLowerCase().startsWith(this.search_term.toLowerCase())
        })
        this.show_results = true
      } else {
        this.show_results = false
      }
    },
    selected_item() {
      if (this.selected_item) {
        this.$emit("chosen", { ...this.selected_item })
        this.search_term = null
        this.search_results = []
        this.show_results = false
        this.selected_item = null
      }
    }
  },
  mounted() {
    this.$refs.mainInput.focus()
    this.show_results = true
    if (this.users.length) {
      this.search_results = this.users
    }
  },
  methods: {
    handleAddNewContact() {
      try {
        let parent_company_domain = this.companies?.length ? new Record(this.companies[0])?.domain_name : null
        let { first_name, last_name, email } = this.$utils.extractContactInfoFromInputWord(this.search_term, parent_company_domain)
        let defaults = {}
        defaults[this.$utils.getOptionMetricReference("contact", "first_name")] = first_name
        defaults[this.$utils.getOptionMetricReference("contact", "last_name")] = last_name
        defaults[this.$utils.getOptionMetricReference("contact", "email")] = email
        const metrics_obj = {
          object_type: "contact",
          page_reference: "portfolio",
          inside_modal: true,
          parent_company: this.companies?.length ? this.companies[0] : "",
          view_type: VIEW_TYPES.FORM_CREATE,
          defaults: defaults
        }
        this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
      } catch (error) {
        const metrics_obj = {
          object_type: "contact",
          page_reference: "portfolio",
          inside_modal: true,
          parent_company: this.companies?.length ? this.companies[0] : "",
          view_type: VIEW_TYPES.FORM_CREATE,
          defaults: {
            first_name: "",
            last_name: "",
            email: ""
          }
        }
        this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
      }
    },
    focusInput() {
      this.isInputFocused = true
    },
    unfocusInput() {
      this.isInputFocused = false
    },
    reset() {
      this.$emit("reset")
    }
  }
}
</script>

<style scoped>
.mh-48 {
  max-height: 10rem;
}
</style>
